import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';
import { useTrip } from '../state/trip';
import Logo from './Logo';

const NAV = [
  { to: '/plan', label: 'Plan a trip' },
  { to: '/explore', label: 'Explore' },
  { to: '/routes', label: 'Routes' },
  { to: '/passport', label: 'Passport' },
  { to: '/owners', label: 'Owners' },
];

export default function Layout() {
  const { trip } = useTrip();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-2.5 text-[15px] font-semibold no-underline transition-colors ${
      isActive ? 'bg-panel-2 text-amber-hi' : 'text-cream hover:bg-panel hover:text-cream'
    }`;

  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 btn btn-primary">
        Skip to content
      </a>

      <header className="sticky top-0 z-[1000] border-b border-line bg-asphalt/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <NavLink to="/" className="flex items-center gap-2.5 text-cream no-underline hover:text-cream">
            <Logo />
            <span className="flex flex-col">
              <span className="font-display text-xl font-black uppercase leading-none tracking-wider sm:text-2xl">Backroad Stop</span>
              <span className="hidden text-[11px] font-semibold uppercase tracking-[0.14em] text-muted sm:block">Bars &amp; bites worth the detour</span>
            </span>
          </NavLink>

          <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass}>
                {n.label}
              </NavLink>
            ))}
            <NavLink to="/my-trip" className="btn btn-primary btn-sm ml-2 no-underline">
              My trip
              <span className="rounded-full bg-amber-ink/15 px-2 text-sm tabular-nums">{trip.stops.length}</span>
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 lg:hidden">
            <NavLink to="/my-trip" className="btn btn-primary btn-sm no-underline">
              Trip <span className="tabular-nums">{trip.stops.length}</span>
            </NavLink>
            <button
              type="button"
              className="btn btn-sm w-11 px-0"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
            </button>
          </div>
        </div>

        {open && (
          <nav aria-label="Main" className="border-t border-line px-4 pb-4 pt-2 lg:hidden">
            <div className="flex flex-col">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className={linkClass}>
                  {n.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-between gap-3 px-4 py-7 text-sm text-dim sm:px-6">
          <span>Hours change, so call ahead. Bar hops need a sober driver, and nobody drives after drinking.</span>
          <span>
            Backroad Stop · Map data ©{' '}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
              OpenStreetMap
            </a>{' '}
            contributors
          </span>
        </div>
      </footer>
    </div>
  );
}
